import React, { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import App from './App.tsx'
import { ClerkProvider } from '@clerk/clerk-react';
import { initDemoModeBodyClass } from './lib/featureFlags';
import { isProduction, showLogs } from './config/env';
import { AppUserProvider } from './context/AppUserContext';
import { ImpersonationProvider } from './context/ImpersonationContext'; // CB:BEGIN impersonation-import
import AppErrorBoundary from './components/AppErrorBoundary'; // CB:BEGIN app-error-boundary

// FIX: Expose build id to the app for update UI
declare global { interface Window { __CB_BUILD_ID__?: string } }
window.__CB_BUILD_ID__ = (typeof __CB_BUILD_ID__ !== 'undefined' ? __CB_BUILD_ID__ : String(Date.now()));

// FIX: Install dynamic import guard overlay at startup
import { mountUpdateOverlay } from './lib/updateOverlay';
mountUpdateOverlay();

// Initialize Demo Mode body class
initDemoModeBodyClass();

// CB:BEGIN silence-prod-logs
if (isProduction && !showLogs) {
  try {
    console.log = () => {};
    console.info = () => {};
    console.debug = () => {};
    console.warn = () => {};
  } catch {}
}
// CB:END silence-prod-logs

// CB:BEGIN suppress-clerk-dev-warning
// Hide the noisy Clerk dev-key warning in console while keeping other warns
(() => {
  const originalWarn = console.warn?.bind(console) || (() => {});
  console.warn = (...args: any[]) => {
    const first = args?.[0];
    if (typeof first === 'string' && first.includes('Clerk has been loaded with development keys')) {
      return;
    }
    originalWarn(...args);
  };
})();
// CB:END suppress-clerk-dev-warning

// CB:BEGIN dev-basic-auth
function DevGate({ children }: { children: React.ReactNode }) {
  const [ok, setOk] = React.useState(() => {
    try { return sessionStorage.getItem('cb_dev_gate') === '1'; } catch { return false; }
  });
  React.useEffect(() => {
    if (ok) return;
    const host = window.location.host;
    const isDevDomain = /dev\.corebids\.com$/i.test(host);
    if (!isDevDomain) { setOk(true); return; }
    const expected = import.meta.env.VITE_DEV_GATE_PASSWORD || '';
    if (!expected) { setOk(true); return; } // No password configured → open access
    // Prompt until correct (session-only)
    const ans = window.prompt('Enter password to access');
    if (ans === expected) {
      try { sessionStorage.setItem('cb_dev_gate', '1'); } catch {}
      setOk(true);
    } else {
      // Block render; allow retry on reload
      // Use textContent instead of innerHTML to prevent XSS
      const div = document.createElement('div');
      div.style.fontFamily = 'sans-serif';
      div.style.padding = '40px';
      div.style.textAlign = 'center';
      div.style.color = '#334155';
      div.textContent = 'Access denied. Refresh to try again.';
      document.body.innerHTML = ''; // Clear body first
      document.body.appendChild(div);
    }
  }, [ok]);
  if (!ok) return null;
  return <>{children}</>;
}
// CB:END dev-basic-auth

// CB:BEGIN hybrid-clerk-config
const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

const clerkPubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
if (!clerkPubKey) {
  throw new Error('VITE_CLERK_PUBLISHABLE_KEY is not set. Add it to your .env or Vercel environment variables.');
}

// Normal Clerk configuration - no bypass
createRoot(document.getElementById('root')!).render(
  <ClerkProvider
    publishableKey={clerkPubKey}
    signInUrl="/sign-in"
    fallbackRedirectUrl="/"
    appearance={{
      elements: {
        formButtonPrimary: "bg-blue-600 hover:bg-blue-700 text-white",
        card: "shadow-lg",
        headerTitle: "text-gray-900",
        headerSubtitle: "text-gray-600",
        socialButtonsBlockButton: "border border-gray-300 hover:bg-gray-50",
        formFieldInput: "border border-gray-300 rounded-md",
        footerActionLink: "text-blue-600 hover:text-blue-700"
      }
    }}
  >
    <StrictMode>
      {/* CB:BEGIN app-error-boundary */}
      <AppErrorBoundary>
      {/* CB:END app-error-boundary */}
      <DevGate>
        {/* CB:BEGIN app-user-provider-root – provide global role/readiness context */}
        <AppUserProvider>
          {/* CB:BEGIN impersonation-provider-root */}
          <ImpersonationProvider>
            <App />
          </ImpersonationProvider>
          {/* CB:END impersonation-provider-root */}
        </AppUserProvider>
        {/* CB:END app-user-provider-root */}
      </DevGate>
      {/* CB:BEGIN app-error-boundary */}
      </AppErrorBoundary>
      {/* CB:END app-error-boundary */}
    </StrictMode>
  </ClerkProvider>
);
// CB:END hybrid-clerk-config